import { useEffect, useRef, useState } from "react";

/** Glyphs the noise is drawn from — skewed toward symbols an engineer would type. */
const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ/\\<>[]{}=+*#$%&";

interface ScrambleProps {
    phrases: string[];
    /** ms a resolved phrase holds before the next one starts */
    hold?: number;
    className?: string;
}

/**
 * Cycles through phrases by resolving each one out of noise, left to right.
 *
 * Deliberately not a typewriter. The blinking-cursor typewriter is the single
 * most over-used effect on developer portfolios — it is the first thing that
 * marks a page as templated — and it reflows the line on every keystroke.
 * This resolves in place, at a fixed width, and reads as a system decoding
 * rather than a person typing.
 *
 * `phrases` must be a stable reference (module-level const), or the effect
 * restarts on every render.
 */
export default function Scramble({ phrases, hold = 2600, className }: ScrambleProps) {
    const [text, setText] = useState(phrases[0]);
    const cursor = useRef(0);

    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

        let raf = 0;
        let timer: ReturnType<typeof setTimeout>;
        let cancelled = false;

        const run = () => {
            const from = phrases[cursor.current];
            const next = (cursor.current + 1) % phrases.length;
            const to = phrases[next];
            cursor.current = next;

            const len = Math.max(from.length, to.length);

            // Each character gets its own start and end frame, so the reveal
            // sweeps across the word instead of the whole line snapping at once.
            // The random tail length is what stops the sweep looking mechanical.
            const starts: number[] = [];
            const ends: number[] = [];
            for (let i = 0; i < len; i++) {
                const s = i * 1.6;
                starts.push(s);
                ends.push(s + 8 + Math.random() * 11);
            }
            const total = Math.max(...ends);

            let frame = 0;
            const step = () => {
                if (cancelled) return;
                let out = "";
                for (let i = 0; i < len; i++) {
                    if (frame >= ends[i]) out += to[i] ?? "";
                    else if (frame >= starts[i]) out += GLYPHS[(Math.random() * GLYPHS.length) | 0];
                    else out += from[i] ?? "";
                }
                setText(out);
                frame++;
                if (frame <= total) {
                    raf = requestAnimationFrame(step);
                } else {
                    setText(to);
                    timer = setTimeout(run, hold);
                }
            };
            step();
        };

        timer = setTimeout(run, hold);
        return () => {
            cancelled = true;
            cancelAnimationFrame(raf);
            clearTimeout(timer);
        };
    }, [phrases, hold]);

    return <span className={className}>{text}</span>;
}
